import { useCallback, useEffect, useState } from 'react';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function usePushNotifications({ savePrefs, prefs } = {}) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && Boolean(VAPID_PUBLIC_KEY);
    setSupported(ok);
    if (typeof Notification !== 'undefined') setPermission(Notification.permission);
    if (!ok) return;
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((s) => setSubscription(s))
    );
  }, []);

  const enable = useCallback(async () => {
    if (!supported) return null;
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') return null;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    setSubscription(sub);
    if (savePrefs) {
      await savePrefs({
        webPushSubscription: sub.toJSON(),
        checkInEnabled: prefs?.checkInEnabled ?? true,
        checkInSchedule: prefs?.checkInSchedule ?? [{ time: '08:00', days: [] }]
      });
    }
    return sub;
  }, [supported, savePrefs, prefs]);

  const disable = useCallback(async () => {
    if (subscription) {
      try {
        await subscription.unsubscribe();
      } catch (err) {
        console.warn(err);
      }
    }
    setSubscription(null);
    if (savePrefs) {
      await savePrefs({ webPushSubscription: null, checkInEnabled: false });
    }
  }, [subscription, savePrefs]);

  return { supported, permission, subscription, enable, disable };
}
